import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { message, Space, Tag, Button, Select } from "antd";
import { EyeOutlined, ExportOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import Layout from "../../../components/Layout";
import { fetchSamples, fetchSample } from "../../../api/labiq/SampleApi";
import { defaultSample, Sample, statusOptions } from "../../../types/lab/SampleTypes";
import { PageContainer, PageToolbar, DataTable } from "../../../components/shared";
import type { ToolbarFilter } from "../../../components/shared/PageToolbar";
import SampleModal from "./components/SampleModal";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { isArray } from "lodash";
import { format } from "date-fns/format";

const allColumns = [
	{ field: "sampleID", header: "Sample ID" },
	{ field: "sampleOA.batch.batch_client_id", header: "Batch ID" },
	...(getSessionUser().company === "DNAIQ" || getSessionUser().company === "Mygene" ? [{ field: "client", header: "Client" }] : []),
	{ field: "sampleOA.analysis_requested", header: "Analysis" },
	{ field: "sampleOA.sampleType", header: "Collection" },
	{ field: "created_at", header: "Created At" },
	{ field: "sampleOA.QC_date", header: "QC Date" },
	{ field: "sampleOA.concentration", header: "ng/μl" },
	{ field: "sampleOA.OD", header: "260/280" },
	{ field: "sampleOA.OD2", header: "260/230" },
	{ field: "sampleOA.status", header: "Status" },
	{ field: "sampleOA.comments", header: "Comments" },
];

const defaultVisibleColumns = ["sampleID", "sampleOA.analysis_requested", "client", "sampleOA.status", "created_at", "sampleOA.QC_date", "sampleOA.comments"].filter(
	(col) => col !== "client" || getSessionUser().company === "DNAIQ" || getSessionUser().company === "Mygene"
);

const SampleTrackingPage: React.FC = () => {
	const [samples, setSamples] = useState<Sample[]>([]);
	const [totalSamples, setTotalSamples] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [sortBy, setSortBy] = useState<string>("created_at");
	const [sortOrder, setSortOrder] = useState<"ascend" | "descend">("descend");
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [selectedSamples, setSelectedSamples] = useState<Sample[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [visibleColumns, setVisibleColumns] = useState<string[]>(defaultVisibleColumns);

	const { id } = useParams<{ id: string }>();

	const SAMPLES_PER_PAGE = loadUserPreferences().tableRowsPerPage;
	const user = useMemo(() => getSessionUser(), []);

	// Single data fetching function using current state
	const fetchSamplesData = useCallback(async () => {
		setIsLoading(true);

		try {
			let sort = sortBy;
			if (isArray(sortBy)) {
				sort = (sortBy as any[]).join(".");
			}

			const { data, total } = await fetchSamples(
				currentPage,
				SAMPLES_PER_PAGE,
				sort === "id" ? "sampleID" : sort,
				sortOrder === "ascend" ? "asc" : "desc",
				searchTerm || undefined,
				statusFilter || undefined
			);

			setSamples(data);
			setTotalSamples(total);
		} catch (err) {
			console.error("Error fetching samples:", err);
			message.error("Failed to load samples.");
		} finally {
			setIsLoading(false);
		}
	}, [currentPage, sortBy, sortOrder, searchTerm, statusFilter, SAMPLES_PER_PAGE, user.company]);

	// Effect to fetch data when dependencies change
	useEffect(() => {
		const loadData = async () => {
			if (id) {
				setIsLoading(true);
				try {
					const sample = await fetchSample(id);
					setSamples([sample]);
					setTotalSamples(1);
					handleEditSample(sample);
				} catch (err) {
					console.error("Error fetching sample:", err);
					message.error("Failed to load sample.");
				} finally {
					setIsLoading(false);
				}
			} else {
				await fetchSamplesData();
			}
		};

		loadData();
	}, [id, fetchSamplesData]);

	// Sort handler
	const handleSort = (field: string, order: "ascend" | "descend" | undefined) => {
		setSortBy(field);
		setSortOrder(order ?? "descend");
	};

	const handleEditSample = (sample: Sample) => {
		setSelectedSamples([sample]);
		setIsModalOpen(true);
	};

	const handleNewSampleClick = () => {
		setSelectedSamples([defaultSample]);
		setIsModalOpen(true);
	};

	const handleModalClose = () => {
		setSelectedSamples([]);
		setIsModalOpen(false);
	};

	const handleFormSubmission = () => {
		handleModalClose();
	};

	const handleSearchChange = (search: string) => {
		setSearchTerm(search);
		setCurrentPage(0);
	};

	const handleStatusFilterChange = (status: string) => {
		setStatusFilter(status);
		setCurrentPage(0);
	};

	const handleReset = () => {
		setSearchTerm("");
		setStatusFilter("");
		setCurrentPage(0);
	};

	// ── Column helpers ─────────────────────────────────────────
	const getStatusColor = (status: string): string => {
		if (["complete", "resultsAvailable"].includes(status)) return "var(--mg-success)";
		if (["processing", "testing", "processingAgrf", "uploadReady", "readyForTesting"].includes(status)) return "var(--mg-primary)";
		if (["received", "receivedByLab", "receivedbylab", "activatedByCustomer", "qcPassed", "dnaExtracted"].includes(status)) return "var(--mg-warning)";
		if (["hold", "destroy", "qcFailed", "resultFailed"].includes(status)) return "var(--mg-danger)";
		return "var(--mg-text-secondary)";
	};

	const getStatusLabel = (status: string): string => {
		const statusMap: Record<string, string> = {
			received: "Received",
			receivedByLab: "At Lab",
			qcPassed: "QC Passed",
			dnaExtracted: "DNA Extracted",
			processing: "Processing",
			testing: "Testing",
			processingAgrf: "External Testing",
			resultAvailable: "Results Ready",
			complete: "Complete",
			hold: "On Hold",
			destroy: "Destroyed",
		};
		return statusMap[status] || status;
	};

	const getConcentrationColor = (concentration: number | null | undefined): string => {
		if (concentration === null || concentration === undefined) return "var(--mg-text-secondary)";
		if (concentration < 15) return "var(--mg-danger)";
		if (concentration < 45) return "var(--mg-warning)";
		return "var(--mg-success)";
	};

	const dateTemplate = (value: string) => {
		if (!value) return "—";
		try {
			return format(new Date(value), "MMM dd, yyyy");
		} catch {
			return "—";
		}
	};

	// ── Table columns (merged from deleted SampleTable component) ──
	const columns: ColumnsType<Sample> = useMemo(() => {
		const cols: ColumnsType<Sample> = [];

		if (visibleColumns.includes("sampleID")) {
			cols.push({
				title: "Sample ID",
				dataIndex: "sampleID",
				key: "sampleID",
				sorter: true,
				width: 120,
				fixed: "left",
			});
		}

		if (visibleColumns.includes("sampleOA.status")) {
			cols.push({
				title: "Status",
				dataIndex: ["sampleOA", "status"],
				key: "sampleOA.status",
				sorter: true,
				width: 130,
				render: (status: string) => {
					const color = getStatusColor(status);
					return (
						<Tag style={{ borderColor: `${color}40`, background: `${color}14`, color, fontSize: 12 }}>
							{getStatusLabel(status)}
						</Tag>
					);
				},
			});
		}

		if (visibleColumns.includes("sampleOA.analysis_requested")) {
			cols.push({
				title: "Analysis",
				dataIndex: ["sampleOA", "analysis_requested"],
				key: "sampleOA.analysis_requested",
				width: 200,
				render: (analyses: string[]) => {
					if (!analyses || analyses.length === 0)
						return <span style={{ color: "var(--mg-text-secondary)" }}>None</span>;
					return (
						<Space size={[4, 4]} wrap>
							{analyses.slice(0, 2).map((analysis, index) => (
								<Tag
									key={index}
									style={{
										borderColor: "var(--mg-primary-border)",
										background: "var(--mg-primary-bg)",
										color: "var(--mg-primary)",
										fontSize: 11,
									}}
								>
									{analysis}
								</Tag>
							))}
							{analyses.length > 2 && (
								<Tag
									style={{
										borderColor: "var(--mg-border)",
										background: "var(--mg-bg-subtle)",
										color: "var(--mg-text-secondary)",
										fontSize: 11,
									}}
								>
									+{analyses.length - 2}
								</Tag>
							)}
						</Space>
					);
				},
			});
		}

		if ((user.company === "DNAIQ" || user.company === "Mygene") && visibleColumns.includes("client")) {
			cols.push({ title: "Client", dataIndex: "client", key: "client", sorter: true, width: 100 });
		}

		if (visibleColumns.includes("sampleOA.sampleType")) {
			cols.push({
				title: "Collection",
				dataIndex: ["sampleOA", "sampleType"],
				key: "sampleOA.sampleType",
				width: 100,
				render: (text: string) => text || "—",
			});
		}

		if (visibleColumns.includes("created_at")) {
			cols.push({ title: "Created", dataIndex: "created_at", key: "created_at", sorter: true, width: 100, render: dateTemplate });
		}

		if (visibleColumns.includes("sampleOA.QC_date")) {
			cols.push({
				title: "QC Date",
				dataIndex: ["sampleOA", "QC_date"],
				key: "sampleOA.QC_date",
				width: 100,
				render: (qcDate: string) => {
					if (!qcDate) return "—";
					const date = new Date(qcDate);
					const daysDiff = Math.floor((Date.now() - date.getTime()) / (1000 * 3600 * 24));
					let color = "var(--mg-primary)";
					if (daysDiff > 14) color = "var(--mg-danger)";
					else if (daysDiff > 7) color = "var(--mg-warning)";
					return (
						<Tag style={{ borderColor: `${color}40`, background: `${color}14`, color }}>
							{format(date, "MMM dd")}
						</Tag>
					);
				},
			});
		}

		if (visibleColumns.includes("sampleOA.concentration")) {
			cols.push({
				title: "Concentration",
				dataIndex: ["sampleOA", "concentration"],
				key: "sampleOA.concentration",
				width: 120,
				render: (concentration: number) => {
					if (concentration === null || concentration === undefined) return "—";
					const color = getConcentrationColor(concentration);
					return (
						<Tag style={{ borderColor: `${color}40`, background: `${color}14`, color }}>
							{concentration} ng/μl
						</Tag>
					);
				},
			});
		}

		if (visibleColumns.includes("sampleOA.OD")) {
			cols.push({ title: "260/280", dataIndex: ["sampleOA", "OD"], key: "sampleOA.OD", width: 80, render: (t: string) => t || "—" });
		}

		if (visibleColumns.includes("sampleOA.OD2")) {
			cols.push({ title: "260/230", dataIndex: ["sampleOA", "OD2"], key: "sampleOA.OD2", width: 80, render: (t: string) => t || "—" });
		}

		if (visibleColumns.includes("sampleOA.comments")) {
			cols.push({
				title: "Comments",
				dataIndex: ["sampleOA", "comments"],
				key: "sampleOA.comments",
				width: 150,
				render: (text: string) => (
					<div style={{ fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{text || "—"}
					</div>
				),
			});
		}

		cols.push({
			title: "Actions",
			key: "actions",
			width: 80,
			fixed: "right",
			render: (_: any, record: Sample) => (
				<Space size="small">
					<Button
						type="text"
						icon={<EyeOutlined />}
						size="small"
						onClick={(e) => { e.stopPropagation(); handleEditSample(record); }}
					/>
					<Button
						type="text"
						icon={<ExportOutlined />}
						size="small"
						onClick={(e) => { e.stopPropagation(); window.open(`/lab/processing?sample=${record.sampleID}`, "_blank"); }}
					/>
				</Space>
			),
		});

		return cols;
	}, [visibleColumns, user.company]);

	// ── Toolbar filters ────────────────────────────────────────
	const filters: ToolbarFilter[] = [
		{
			type: "search",
			key: "search",
			placeholder: "Search sample ID, client…",
			value: searchTerm,
			onChange: handleSearchChange,
			width: 240,
		},
		{
			type: "select",
			key: "status",
			placeholder: "All Statuses",
			value: statusFilter,
			options: statusOptions,
			onChange: handleStatusFilterChange,
			width: 180,
		},
		{
			type: "custom",
			key: "columns",
			render: (
				<Select
					mode="multiple"
					value={visibleColumns}
					options={allColumns.map((col) => ({ label: col.header, value: col.field }))}
					onChange={(value) => setVisibleColumns(value)}
					placeholder="Columns"
					maxTagCount={2}
					style={{ minWidth: 200 }}
				/>
			),
		},
	];

	return (
		<Layout>
			<PageContainer >
				<PageToolbar
					primaryAction={{ label: "New Sample", onClick: handleNewSampleClick }}
					filters={filters}
					onReset={handleReset}
				/>
				<DataTable
					columns={columns}
					dataSource={samples}
					rowKey="id"
					loading={isLoading}
					total={totalSamples}
					currentPage={currentPage + 1}
					pageSize={SAMPLES_PER_PAGE}
					onPageChange={(page) => setCurrentPage(page - 1)}
					sortField={sortBy}
					sortOrder={sortOrder}
					onSort={handleSort}
					onRowClick={handleEditSample}
					fillHeight
					emptyTitle="No samples found"
					emptyDescription="Adjust your filters or create a new sample"
				/>
			</PageContainer>

			{isModalOpen && selectedSamples.length === 1 && (
				<SampleModal isOpen={isModalOpen} onClose={handleModalClose} sample={selectedSamples[0]} patient={null} onFormSubmission={handleFormSubmission} />
			)}
		</Layout>
	);
};

export default SampleTrackingPage;
